import { Element } from 'react-scroll';
import Moment from 'react-moment';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';

import { isMobile } from '../../../../../mobile/helpers';
import styles from './activity.scss';

const propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_promoted: PropTypes.number,
    title: PropTypes.string,
    time: PropTypes.string,
    time_end: PropTypes.string,
    area: PropTypes.shape({
      name: PropTypes.string,
      pin: PropTypes.string,
    }),
    main_tag: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    tags: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    accelerated: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func,
  hasNotification: PropTypes.bool,
  hasPersonal: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onToggleNotification: PropTypes.func.isRequired,
  style: PropTypes.shape({
    position: PropTypes.string,
  }),
  push: PropTypes.func.isRequired,
  match: PropTypes.shape().isRequired,
  hideNotification: PropTypes.bool,
  localTimezone: PropTypes.string,
};

const hasPassed = (activity) => (
  ((activity.time_end && moment.utc(activity.time_end))
      || (moment.utc(activity.time).add(1, 'hours'))
  ).isBefore(moment().utc())
);

const isActive = (activity) => (
  moment.utc(activity.time).isBefore(moment().utc())
    && ((activity.time_end && moment.utc(activity.time_end))
      || (moment.utc(activity.time).add(1, 'hours'))
    ).isAfter(moment().utc())
);

const Control = ({
  active,
  onClick,
  icon,
  disabled,
}) => (
  <div
    className={classNames(
      styles.radio,
      {
        [styles.disabled]: disabled,
      },
    )}
    role="button"
    tabIndex={0}
    onClick={(event) => {
      event.stopPropagation();
      return !disabled ? onClick(event) : null;
    }}
  >
    <div
      className={classNames(
        {
          [styles.checked]: active,
        },
      )}
    >
      { active
        ? <i className={`far fa-${icon}`} />
        : <i className={`far fa-${icon}`} />}
    </div>
  </div>
);
Control.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

const Activity = ({
  activity,
  onAdd,
  onRemove,
  onClick,
  hasNotification,
  hasPersonal,
  onToggleNotification,
  style,
  push,
  hideNotification,
  match,
  localTimezone,
}) => {
  const start = activity && moment.parseZone(activity.time);
  const isNotSameTimeZone = start && (
    start.utcOffset() !== start.tz(localTimezone).utcOffset()
  );

  return (
    /* Note <Element> is a react-scroll component needed to scroll! */
    <Element
      key={activity.id}
      name={`activity-${activity.id}`}
      className={classNames(
        styles.activity,
        {
          [`${styles.promoted}`]: activity.is_promoted,
          /* If there is an end time, the activity is passed after it.
           * If not, add one hour to start time for an artificial end time */
          [`${styles.passed}`]: hasPassed(activity),
        },
      )}
      style={style}

      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div
        className={styles.info}
      >
        <div className={styles.name}>
          { activity.is_promoted
            ? (
              <span className={styles.highlight}>
                <i className="far fa-star" />
              </span>
            ) : null}
          {activity.title}
        </div>
        {activity.area && activity.area.name
          && (
          <div className={styles.location}>
            <i className="far fa-map-marker-alt" />
            {' '}
            {activity.area && activity.area.name}
          </div>
          )}
        <div className={styles.time}>
          <i className="far fa-clock" />
          {' '}
          <Moment format={isNotSameTimeZone ? `LT [${localTimezone || ''}] · dddd` : 'LT · dddd'}>{activity.time}</Moment>
          { !!activity.accelerated && (
            <span className={styles.acceleratedWarning}>
              <i className="fad fa-exclamation" />
            </span>
          )}
          { isActive(activity) && (
            <span className={styles.isActive}>
              {' · Now '}
              {activity.time_end && <>(<Moment fromNow ago>{activity.time_end}</Moment> left)</>}
            </span>
          )}
        </div>
      </div>
      <div className={styles.controls}>
        <Control
          active={hasPersonal}
          icon="star"
          onClick={() => (
            hasPersonal
              ? onRemove() : onAdd()
          )}
        />
        { isMobile() && !hideNotification
          && (
          <Control
            active={hasNotification}
            icon="bell"
            onClick={onToggleNotification}
            disabled={!moment(activity.time).subtract(5, 'minute').isAfter(moment().utc())}
          />
          )}
        <Control
          icon="map-marker-alt"
          onClick={() => {
            push(`${match.url}/map/${activity.id}`);
          }}
          disabled={!activity.area || !activity.area.pin}
        />
        { navigator && navigator.share && (
          <Control
            icon="share"
            onClick={() => navigator.share({
              title: activity.title,
              text: activity.title,
              url: `https://dreamhack.com/app/event/${match.params.shortcode}/schedule/${activity.id}`,
            })}
          />
        )}
      </div>
      { /*
      <div
        className={classNames({
          [`${styles.image}`]: activity.image_id,
        })}
        style={{ backgroundImage: `url(${getImageUrl(activity.image_id)})` }}
      />
      */ }
    </Element>
  );
};

Activity.propTypes = propTypes;

export default Activity;
